/**
 * <sort-dropdown> — a custom, filter-styled sort control.
 *
 * Progressive enhancement over a real <select name="sort_by">. Selecting an
 * option syncs the native <select> and dispatches `input`/`change` so the
 * existing facet-filters-form listener (facets.js) runs its AJAX sort exactly
 * as it does for the native control. If this script never loads, the native
 * select (and the no-JS sort form) still work.
 */
if (!customElements.get('sort-dropdown')) {
  class SortDropdown extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.select = this.querySelector('.sort-dropdown__native');
      this.trigger = this.querySelector('.sort-dropdown__trigger');
      this.panel = this.querySelector('.sort-dropdown__panel');
      this.valueEl = this.querySelector('[data-sort-value]');
      this.options = Array.from(this.querySelectorAll('.sort-dropdown__option'));

      if (!this.select || !this.trigger || !this.panel) return;

      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e));
      this.panel.addEventListener('keydown', (e) => this.onPanelKeydown(e));
      this.options.forEach((opt) => {
        opt.addEventListener('click', () => this.choose(opt));
      });
    }

    get visibleOptions() {
      return this.options.filter((option) => !option.hidden);
    }

    get isOpen() {
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    open() {
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      document.addEventListener('click', this.onDocumentClick);
      const target = this.visibleOptions.find((option) => option.classList.contains('is-selected')) || this.visibleOptions[0];
      if (target) target.focus();
    }

    close() {
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      document.removeEventListener('click', this.onDocumentClick);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.open();
      } else if (event.key === 'Escape') {
        this.close();
      }
    }

    onPanelKeydown(event) {
      const current = document.activeElement;
      const options = this.visibleOptions;
      if (!options.length) return;
      const index = options.indexOf(current);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        (options[index + 1] || options[0]).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        (options[index - 1] || options[options.length - 1]).focus();
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (current && current.classList.contains('sort-dropdown__option')) this.choose(current);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.trigger.focus();
      }
    }

    choose(option) {
      if (option.hidden) return;
      const value = option.getAttribute('data-value');

      if (this.select.value !== value) {
        this.select.value = value;

        this.options.forEach((opt) => {
          const selected = opt === option;
          opt.classList.toggle('is-selected', selected);
          opt.setAttribute('aria-selected', selected ? 'true' : 'false');
        });

        const labelEl = option.querySelector('.sort-dropdown__option-label');
        if (this.valueEl && labelEl) this.valueEl.textContent = labelEl.textContent.trim();

        // Hand off to the existing facet-filters-form AJAX (listens for `input`).
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.close();
      this.trigger.focus();
    }
  }

  customElements.define('sort-dropdown', SortDropdown);
}
